import { useAtom } from 'jotai';
import { useMemo } from 'react';

import { getSearchableSelectOptions } from '@/features/SearchBar';
import { originalSourceTypesAtom } from '@/features/SourceCreate/SourceCreate.store';
import { LIMA_SOURCE_QUICK_FILTERS } from '@/pages/Administration/Devices/Devices.constants';
import {
  getLimaSourceFilterOptions,
  getLimaSourceStatusFilterOptions
} from '@/pages/Administration/Devices/Devices.service';
import { cloudSourcesAtom } from '@/pages/Administration/Devices/Devices.store';

/**
 * Custom hook for computing Lima cloud source quick filter options.
 *
 * Generates dynamic filter options for the search bar based on:
 * - **Source Type**: Available source types with active sources
 * - **Status**: Current source statuses present in data
 * - **Other Fields**: Dynamic options extracted from cloud sources data
 *
 * Filter options are computed from atoms (sourceTypes and cloudSourcesData) and
 * are automatically updated when data changes.
 *
 * @returns Object containing:
 * - `limaSourcesQuickFilters` - Array of searchable select filters with computed options
 *
 * @example
 * ```tsx
 * const { limaSourcesQuickFilters } = useLimaQuickFilters();
 * <SearchBar searchableSelectFilters={limaSourcesQuickFilters} />
 * ```
 */
export const useLimaQuickFilters = () => {
  const [sourceTypes] = useAtom(originalSourceTypesAtom);
  const [cloudSourcesData] = useAtom(cloudSourcesAtom);

  const limaSourcesQuickFilters = useMemo(() => {
    return LIMA_SOURCE_QUICK_FILTERS.map((filter) => {
      let filterOptions = [];
      if (filter.field === 'source_type_id') {
        filterOptions = getLimaSourceFilterOptions(
          sourceTypes,
          cloudSourcesData
        );
      } else if (filter.field === 'status') {
        filterOptions = getLimaSourceStatusFilterOptions(cloudSourcesData);
      } else {
        filterOptions = getSearchableSelectOptions(
          cloudSourcesData,
          filter.field,
          filter.title
        );
      }
      return {
        ...filter,
        filterOptions: [...filter.filterOptions, ...filterOptions]
      };
    });
  }, [sourceTypes, cloudSourcesData]);

  return {
    limaSourcesQuickFilters
  };
};
